import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  paymentsService,
  PaymentPlan,
  OrgSubscription,
  Invoice,
  PaymentsConfig,
  formatMoney,
} from '../services/paymentsService';

const PaymentsPage: React.FC = () => {
  const [params] = useSearchParams();
  const status = params.get('status'); // canceled, success
  const [config, setConfig] = useState<PaymentsConfig | null>(null);
  const [plans, setPlans] = useState<PaymentPlan[]>([]);
  const [subscription, setSubscription] = useState<OrgSubscription | null>(null);
  const [currentPlan, setCurrentPlan] = useState<PaymentPlan | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState<string | null>(
    status === 'canceled' ? 'Checkout was canceled.' : null,
  );

  const load = useCallback(async () => {
    setError(null);
    try {
      const [cfg, plans, sub, inv] = await Promise.all([
        paymentsService.getConfig(),
        paymentsService.listPlans(false),
        paymentsService.getSubscription(),
        paymentsService.listInvoices(),
      ]);
      setConfig(cfg);
      setPlans(plans);
      setSubscription(sub.subscription);
      setCurrentPlan(sub.plan);
      setInvoices(inv);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load payments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const subscribe = async (planId: number) => {
    setBusy(true);
    setError(null);
    try {
      const { checkout_url } = await paymentsService.subscribe(planId);
      window.location.href = checkout_url;
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to start checkout');
      setBusy(false);
    }
  };

  const portal = async () => {
    setBusy(true);
    setError(null);
    try {
      const url = await paymentsService.openPortal();
      window.location.href = url;
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to open portal');
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!confirm('Cancel at the end of the current billing period?')) return;
    setBusy(true);
    try {
      const updated = await paymentsService.cancel();
      setSubscription(updated);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to cancel');
    } finally {
      setBusy(false);
    }
  };

  const resume = async () => {
    setBusy(true);
    try {
      const updated = await paymentsService.resume();
      setSubscription(updated);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to resume');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="p-6 text-sm text-[var(--color-text-secondary)]">Loading…</div>;

  const isActive = subscription && ['active', 'trialing', 'past_due'].includes(subscription.status);

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">Subscription &amp; Billing</h1>
        <p className="text-sm text-[var(--color-text-secondary)] mt-1">
          Manage your organization's subscription. Powered by Stripe.
        </p>
      </div>

      {!config?.stripe_configured && (
        <div
          className="p-3 rounded-md text-sm"
          style={{
            backgroundColor: 'var(--color-status-error-light, #fef2f2)',
            color: 'var(--color-error)',
          }}
        >
          Stripe is not configured on the server. Set <code>STRIPE_SECRET_KEY</code> (and{' '}
          <code>STRIPE_WEBHOOK_SECRET</code> for webhooks) in the backend environment to enable
          checkout. Listing plans and viewing past invoices still works.
        </div>
      )}
      {info && (
        <div
          className="p-3 rounded-md text-sm"
          style={{ backgroundColor: 'var(--color-background-surface)', color: 'var(--color-text-secondary)' }}
        >
          {info}
        </div>
      )}
      {error && (
        <div
          className="p-3 rounded-md text-sm"
          style={{
            backgroundColor: 'var(--color-status-error-light, #fef2f2)',
            color: 'var(--color-error)',
          }}
        >
          {error}
        </div>
      )}

      {/* Current subscription */}
      <section
        className="rounded-lg border p-5"
        style={{
          backgroundColor: 'var(--color-background-card)',
          borderColor: 'var(--color-border-default)',
        }}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-[var(--color-text-muted)]">
              Current subscription
            </div>
            <div className="mt-1 flex items-center gap-2">
              <span className="text-xl font-bold text-[var(--color-text-primary)]">
                {currentPlan ? currentPlan.name : 'No active plan'}
              </span>
              {subscription && <StatusBadge status={subscription.status} />}
              {subscription?.cancel_at_period_end && (
                <span
                  className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase"
                  style={{ backgroundColor: 'var(--color-warning)' + '22', color: 'var(--color-warning)' }}
                >
                  cancels at period end
                </span>
              )}
            </div>
            {currentPlan && (
              <div className="text-sm text-[var(--color-text-secondary)] mt-1">
                {formatMoney(currentPlan.amount_cents, currentPlan.currency)} /{' '}
                {currentPlan.billing_interval}
              </div>
            )}
            {subscription?.current_period_end && (
              <div className="text-xs text-[var(--color-text-muted)] mt-1">
                {subscription.cancel_at_period_end ? 'Ends' : 'Renews'} on{' '}
                {new Date(subscription.current_period_end).toLocaleDateString()}
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2 items-end">
            {isActive && (
              <>
                <button
                  type="button"
                  disabled={busy || !config?.stripe_configured}
                  onClick={portal}
                  className="px-4 py-2 text-sm font-medium rounded-md bg-[var(--color-primary)] text-[var(--color-text-inverse)] disabled:opacity-50 hover:opacity-90"
                  data-testid="open-portal"
                >
                  Manage in Stripe
                </button>
                {subscription?.cancel_at_period_end ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={resume}
                    className="px-3 py-1.5 text-xs rounded-md border border-[var(--color-border-default)] hover:bg-[var(--color-background-surface)] disabled:opacity-50"
                  >
                    Resume
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={cancel}
                    className="px-3 py-1.5 text-xs rounded-md text-[var(--color-error)] border border-[var(--color-border-default)] hover:bg-[var(--color-status-error-light)] disabled:opacity-50"
                  >
                    Cancel at period end
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      {/* Plans */}
      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-[var(--color-text-muted)] mb-3">
          {isActive ? 'Switch plan' : 'Available plans'}
        </h2>
        {plans.length === 0 ? (
          <div
            className="p-6 rounded-lg border text-sm text-center text-[var(--color-text-secondary)]"
            style={{
              borderColor: 'var(--color-border-default)',
              backgroundColor: 'var(--color-background-card)',
            }}
          >
            No plans configured. A system admin can create one via{' '}
            <code className="text-xs">POST /api/v1/payments/plans</code>.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {plans.map((p) => {
              const isCurrent = currentPlan?.id === p.id;
              return (
                <div
                  key={p.id}
                  className="rounded-lg border p-4 flex flex-col"
                  style={{
                    borderColor: isCurrent ? 'var(--color-primary)' : 'var(--color-border-default)',
                    backgroundColor: 'var(--color-background-card)',
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="text-lg font-semibold text-[var(--color-text-primary)]">
                      {p.name}
                    </div>
                    {isCurrent && (
                      <span
                        className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase"
                        style={{
                          backgroundColor: 'var(--color-primary)' + '22',
                          color: 'var(--color-primary)',
                        }}
                      >
                        Current
                      </span>
                    )}
                  </div>
                  {p.description && (
                    <div className="text-sm text-[var(--color-text-secondary)] mt-1">{p.description}</div>
                  )}
                  <div className="mt-3 mb-4">
                    <span className="text-3xl font-bold text-[var(--color-text-primary)]">
                      {formatMoney(p.amount_cents, p.currency)}
                    </span>
                    <span className="text-sm text-[var(--color-text-secondary)]"> / {p.billing_interval}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => subscribe(p.id)}
                    disabled={busy || isCurrent || !config?.stripe_configured}
                    className="mt-auto px-4 py-2 text-sm font-medium rounded-md bg-[var(--color-primary)] text-[var(--color-text-inverse)] disabled:opacity-40 hover:opacity-90"
                    data-testid={`subscribe-${p.id}`}
                  >
                    {isCurrent ? 'Active' : isActive ? 'Switch to this plan' : 'Subscribe'}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Invoice history */}
      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-[var(--color-text-muted)] mb-3">
          Invoices
        </h2>
        {invoices.length === 0 ? (
          <div
            className="p-6 rounded-lg border text-sm text-center text-[var(--color-text-secondary)]"
            style={{
              borderColor: 'var(--color-border-default)',
              backgroundColor: 'var(--color-background-card)',
            }}
          >
            No invoices yet.
          </div>
        ) : (
          <div
            className="rounded-lg border overflow-hidden"
            style={{
              borderColor: 'var(--color-border-default)',
              backgroundColor: 'var(--color-background-card)',
            }}
          >
            <table className="w-full text-sm">
              <thead>
                <tr
                  className="text-left text-xs uppercase text-[var(--color-text-muted)]"
                  style={{ backgroundColor: 'var(--color-background-surface)' }}
                >
                  <th className="px-4 py-2 font-semibold">Date</th>
                  <th className="px-4 py-2 font-semibold">Description</th>
                  <th className="px-4 py-2 font-semibold text-right">Amount</th>
                  <th className="px-4 py-2 font-semibold">Status</th>
                  <th className="px-4 py-2 font-semibold">Receipt</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-t" style={{ borderColor: 'var(--color-border-light)' }}>
                    <td className="px-4 py-2 text-[var(--color-text-secondary)]">
                      {new Date(inv.paid_at ?? inv.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-2 text-[var(--color-text-primary)]">{inv.description ?? '—'}</td>
                    <td className="px-4 py-2 text-right text-[var(--color-text-primary)]">
                      {formatMoney(inv.amount_cents, inv.currency)}
                    </td>
                    <td className="px-4 py-2">
                      <PaymentStatusBadge status={inv.status} />
                    </td>
                    <td className="px-4 py-2">
                      {inv.hosted_invoice_url || inv.receipt_url ? (
                        <a
                          className="text-xs text-[var(--color-text-link)] hover:underline"
                          href={inv.hosted_invoice_url ?? inv.receipt_url ?? '#'}
                          target="_blank"
                          rel="noreferrer"
                        >
                          View
                        </a>
                      ) : (
                        <span className="text-xs text-[var(--color-text-muted)]">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const map: Record<string, { bg: string; fg: string }> = {
    active: { bg: 'var(--color-success)' + '22', fg: 'var(--color-success)' },
    trialing: { bg: 'var(--color-info, #3b82f6)' + '22', fg: 'var(--color-info, #3b82f6)' },
    past_due: { bg: 'var(--color-warning)' + '22', fg: 'var(--color-warning)' },
    canceled: { bg: 'var(--color-text-muted)' + '22', fg: 'var(--color-text-muted)' },
    incomplete: { bg: 'var(--color-warning)' + '22', fg: 'var(--color-warning)' },
    inactive: { bg: 'var(--color-text-muted)' + '22', fg: 'var(--color-text-muted)' },
  };
  const c = map[status] ?? map.inactive;
  return (
    <span
      className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase"
      style={{ backgroundColor: c.bg, color: c.fg }}
    >
      {status}
    </span>
  );
};

const PaymentStatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const map: Record<string, { bg: string; fg: string }> = {
    paid: { bg: 'var(--color-success)' + '22', fg: 'var(--color-success)' },
    pending: { bg: 'var(--color-warning)' + '22', fg: 'var(--color-warning)' },
    failed: { bg: 'var(--color-error)' + '22', fg: 'var(--color-error)' },
    refunded: { bg: 'var(--color-text-muted)' + '22', fg: 'var(--color-text-muted)' },
  };
  const c = map[status] ?? { bg: 'var(--color-text-muted)' + '22', fg: 'var(--color-text-muted)' };
  return (
    <span
      className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase"
      style={{ backgroundColor: c.bg, color: c.fg }}
    >
      {status}
    </span>
  );
};

export default PaymentsPage;
